import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthenticationService, UserService } from "app/auth/service";
import { UtilsService } from "app/auth/service/utils.service";
import { environment } from "environments/environment";
import moment from "moment";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject } from "rxjs";
import { io } from "socket.io-client";
import UserOptionsService from "../../auth/service/user-options.service";

@Injectable({ providedIn: "root" })
export class ChatService {
  public contacts: any = [];
  public chats: any[];
  public userProfile;
  public isChatOpen: Boolean;
  public chatUsers: any[];
  public selectedChat;
  public selectedChatUser;
  public chatNotification: number = 0;

  public createChatSender = null;
  public createChatReciver = null;

  public onContactsChange: BehaviorSubject<any>;
  public onChatsChange: BehaviorSubject<any>;
  public onSelectedChatChange: BehaviorSubject<any>;
  public onSelectedChatUserChange: BehaviorSubject<any>;
  public onChatUsersChange: BehaviorSubject<any>;
  public onChatOpenChange: BehaviorSubject<Boolean>;
  public onUserProfileChange: BehaviorSubject<any>;
  public onChatNotificationChange: BehaviorSubject<number>;
  private socket;

  constructor(
    private _authService: AuthenticationService,
    private _userService: UserService,
    private _utilService: UtilsService,
    private router: Router,
    private toastrService: ToastrService,
    private _userOptionsService: UserOptionsService,
  ) {
    this.isChatOpen = false;
    this.onContactsChange = new BehaviorSubject([]);
    this.onChatsChange = new BehaviorSubject([]);
    this.onSelectedChatChange = new BehaviorSubject([]);
    this.onSelectedChatUserChange = new BehaviorSubject([]);
    this.onChatUsersChange = new BehaviorSubject([]);
    this.onChatOpenChange = new BehaviorSubject(false);
    this.onUserProfileChange = new BehaviorSubject([]);
    this.onChatNotificationChange = new BehaviorSubject(0);

    this.userProfile = this.getCurrentUserProfile();
    if (!this.socket) {
      this.socket = io(environment.SOCKET_URL, {
        reconnectionDelayMax: 20000,
        reconnectionDelay: 15000,
      });
    }

    //Ascolto le stanze dell'utente
    this.socket.emit("rooms_list", this.userProfile.hash);
    this.socket.on("rooms_list", (rooms) => {
      //Mi prendo le stanze
      this.chats = rooms.rooms;
      this.onChatsChange.next(this.chats);
      this.getContacts();
    });

    //Evento quando viene creata una nuova stanza
    this.socket.on(`new_room/${this.userProfile.hash}`, (roomId) => {
      //Aggiorno la chat al ricevente e non a chi sta creando la chat
      if (!this.createChatReciver && !this.createChatSender) {
        //Nuova chat per l'utente ricevente
        this.socket.emit("rooms_list", this.userProfile.hash);
        this.addOneChatNotification(true);
      } else {
        //Aggiorno le chat di chi sta creando la chat
        const chatId = roomId;
        if (chatId) {
          //Mando al back-end la nuova chat
          this.createChatSender.room_id = chatId;
          this.socket.emit("send_message", this.createChatSender);
          //pusho la nuova chat cosi da non richiamare il back-end
          this.chats.push({
            messages: [],
            users: [{ hash: this.createChatReciver.hash, avatar: "", status: "online", complete_name: this.createChatReciver.complete_name }],
            id: chatId,
            unread_messages: 0,
            create_date: moment(),
          });
          this.onChatsChange.next(this.chats);
          //Aggiorno la lista delle chat
          this.getChatUsers();
          //Seleziono la chat creata nella lista delle chat e resetto i valori di createChatReciver e createChatSender
          setTimeout(() => {
            this.selectedChats(this.createChatReciver.hash);

            this.createChatReciver = null;
            this.createChatSender = null;
          });
        }
      }
    });

    //Ascolto le risposte che arriveranno soltanto ai riceventi
    this.socket.on("reply", (reply) => {
      //Aggiorno le notifiche generali se l'utente non ha la chat aperta del nuovo messaggio
      if (!this.selectedChat || (this.selectedChat && reply.room_id !== this.selectedChat.id)) {
        this.addOneChatNotification(false);
        const chatToUpdate = this.chatUsers.find((chatUser) => chatUser.hash === reply.sender_hash);
        if (chatToUpdate) {
          if (chatToUpdate) {
            if (!chatToUpdate.unread_messages) {
              chatToUpdate.unread_messages = 1;
            } else {
              chatToUpdate.unread_messages += 1;
            }
          }
          //Mando a schermo la notifica se l'utente non si trova nella stessa pagina
          if (this.router.url !== "/dashboard/chat" && !!reply.message && typeof reply.message == "string") {
            this.toastrService.info(reply.message.length > 30 ? `${reply.message.substring(0, 30)}...` : reply.message, chatToUpdate.complete_name, {
              closeButton: true,
            });
          }
        }
      } else {
        //Nel caso ho la chat aperta setto i messaggi in entrata ha letti(cosi il back-end sa che li ho letti)
        this.socket.emit("read_messages", this.selectedChat.id, this.userProfile.hash);
      }
      this.updateLastMessageSideBar(reply.sender_hash, reply.message, true);

      //Pusho il nuovo messaggio nella chat corretta
      const chat = this.chats.find((c) => c.id === reply.room_id);
      if (chat && chat.messages && chat.messages.length > 0) {
        chat.messages.push(reply);
        this.onChatsChange.next(this.chats);
      }
    });

    this.onUserProfileChange.next(this.userProfile);
  }

  getCurrentUserProfile() {
    return this._authService.getCurrentUser();
  }

  getSelectedChatUser(hash: string) {
    this.selectedChatUser = this.contacts.find((contact) => contact.hash === hash);
    this.onSelectedChatUserChange.next(this.selectedChatUser);
    //Qui appena l'utente cambia chat mi vado a prendere i messaggi di quella stanza e il back-end toglie il numero di messaggi non letti
    if (this.selectedChat && this.selectedChat.id) {
      this.socket.emit("single_room", this.selectedChat.id);
      this.socket.on("single_room", (room) => {
        const chat = this.chats.find((c) => c.id === room.id);
        if (chat) {
          chat.messages = room.messages;
        }
      });
    }
  }

  async getChatUsers() {
    this.chatUsers = this.contacts.filter((contact) => {
      if (contact.hash !== this.userProfile.hash) {
        return this.chats.some((chat) => {
          return chat.users.find((user) => user.hash === contact.hash);
        });
      }
    });
    this.chatUsers.map((item) => {
      let chat = this.chats.find((object) => {
        return object.users.find((user) => user.hash === item.hash);
      });
      if (chat) {
        item.unread_messages = chat.unread_messages;
        item.last_message = chat.last_message;
        //Setto il numero generale delle notifiche non lette
        this.addNotificationNumber(chat.unread_messages);
      }
      this.onChatUsersChange.next(this.chatUsers);
    });
  }

  selectedChats(hash) {
    const selectChat = this.chats.find((chat) => chat.users.some((user) => user.hash === hash));
    // If Chat is Avaiable of Selected hash
    if (selectChat !== undefined) {
      this.selectedChat = selectChat;

      this.onSelectedChatChange.next(this.selectedChat);
      this.getSelectedChatUser(hash);
    }
    // Else Create New Chat
    else {
      const newChat = {
        hash: hash,
        unseenMsgs: 0,
      };
      this.onSelectedChatChange.next(newChat);
      this.getSelectedChatUser(hash);
    }
    //setto i messaggi ha letti al back-end
    if (this.selectedChat && this.selectedChat.id) {
      this.socket.emit("read_messages", this.selectedChat.id, this.userProfile.hash);
    }
  }

  async createNewChat(chatUser, chat) {
    if (chat.message !== "") {
      const findChat = this.chats.find(() => chat?.users?.some((user) => user.hash === chat.hash));
      if (findChat) {
        //Se gia presente la chat pusho soltanto il nuovo messaggio
        findChat.messages.push({ sender_hash: chatUser.hash, message: chat.message });
      } else {
        //Mando i dati per prendermi la stanza
        this.socket.emit("new_room", [chatUser.hash, chat.sender_hash]);
        this.createChatReciver = chatUser;
        this.createChatSender = chat;
      }
      setTimeout(() => {
        this.updateLastMessageSideBar(chatUser.hash, chat.message);
      }, 1000);
    }
  }

  openChat(hash) {
    this.isChatOpen = true;
    this.onChatOpenChange.next(this.isChatOpen);
    this.selectedChats(hash);
  }

  updateChat(chat, chatUserHash) {
    this.socket.emit("send_message", chat);
    this.updateLastMessageSideBar(chatUserHash, chat.message);
  }

  updateUserProfile(userProfileRef) {
    this.userProfile = userProfileRef;
    this._userOptionsService.set("status", userProfileRef.status).subscribe();
    localStorage.setItem("currentUser", JSON.stringify(userProfileRef));
    this._authService.currentUserSubject.next(userProfileRef);
    this.onUserProfileChange.next(this.userProfile);
  }

  async getContacts() {
    if (!this.getContacts || this.getContacts.length < 1) {
      this.contacts = await this._userService.addressBook();
      this.contacts = this.contacts.filter((contact) => contact.hash !== this.userProfile.hash);
      this.onContactsChange.next(this.contacts);
      this.getChatUsers();
    }
  }

  addOneChatNotification(isNewChat: boolean = false) {
    //Aggiungo il + alla chat quando non è una nuova chat, perchè quando è una nuova chat lo aggiungo dalla funzione getChatUsers()
    if (isNewChat) {
      this._utilService.showToastrSuccess("NEW_CHAT_OPENED");
    } else {
      this.onChatNotificationChange.next((this.chatNotification += 1));
    }
  }

  addNotificationNumber(n: number) {
    this.onChatNotificationChange.next((this.chatNotification += n));
  }

  subTractNotificationNumber(n: number) {
    this.onChatNotificationChange.next(this.chatNotification - n);
  }

  updateLastMessageSideBar(chatUserHash: string, message: string, isFromNotThisUser: boolean = false) {
    const chatToUpdate = this.chatUsers.find((user) => user.hash === chatUserHash);
    if (chatToUpdate) {
      //Inserisco l'ultimo messaggio per visualizzarlo nella sidebar
      chatToUpdate.last_message = {
        message: message,
        create_date: moment(),
        sender_hash: isFromNotThisUser ? chatUserHash : this.userProfile.hash,
      };
    }
  }
}
